// Vulnerability Class
// --------------------
// Mirrors the back end or graph call
// Supposed to be a simple storage container
//
// Class imports
import { convertFromGraphAPItoUI } from "@/utils/transform";
import { EMPTY_STRING, formatVulnCategory } from "../constants/definitions";

// SubFinding — replaces the legacy flat `details` array as the BE's
// primary container for what was wrong on this vuln. Every vuln has
// at least one sub-finding after the BE refactor; single-finding
// pentesters (XSS, SQLi, …) auto-derive a single entry from their
// existing fields, multi-finding pentesters (DNS, TLS, Information
// Disclosure, …) emit one entry per observation.
//
// title / description / riskLevel are always present; everything else
// is optional and omitted from the payload entirely when empty —
// consumers should check via `field in subFinding`-equivalent
// (i.e. `!= null && !== ""`) rather than treating absence as
// distinct from emptiness.
//
// riskLevel is the RiskLevel enum NAME ("Critical" | "High" |
// "Medium" | "Low" | "Information" | "Unclassified") emitted as a
// string, NOT the numeric enum value.
// One HTTP header line — name/value pair. Emitted for both request and
// response headers on an observed HTTP exchange. The wire type is
// `HttpHeader` in the schema.
export interface HttpHeader {
  name: string;
  value: string;
}

// One observed HTTP request/response exchange attached to a sub-finding
// as customer-facing evidence. Rendered by the FE as a Postman-style card
// with Baseline/Payload/Control labelling for SQLi variants and similar
// three-request findings.
//
// Only `method`, `url`, and `responseStatus` are guaranteed; every other
// field is optional and absent when the pentester didn't capture it (e.g.
// legacy fingerprint records predate responseBodyExcerpt capture). Render
// the empty state honestly — never fabricate content.
//
// responseBodyExcerpt is truncated to 2 KB by the pentester;
// responseBodyTruncated=true signals the FE to show the truncation notice.
// Added 2026-07-09.
export interface HttpExchange {
  label?: string;
  method: string;
  url: string;
  requestHeaders?: HttpHeader[];
  requestBody?: string;
  responseStatus: number;
  responseHeaders?: HttpHeader[];
  responseBodyExcerpt?: string;
  responseBodyTruncated?: boolean;
}

export interface SubFinding {
  title: string;
  description: string;
  riskLevel: string;
  affectedRecord?: string;
  expected?: string;
  actual?: string;
  referenceLink?: string;
  // Server-computed navigability verdict for referenceLink (backend
  // models/url_safety.py). True only for http/https URLs; the UI renders
  // referenceLink as a live link only when this is true, otherwise as
  // inert text, so a javascript:/data: reference is shown but never
  // executed. Absent on legacy payloads — the FE falls back to its own
  // isSafeHttpUrl check. See SECURITY_AUDIT_FINDINGS F1/F2.
  referenceLinkNavigable?: boolean;
  stepsToReproduce?: string;
  recommendation?: string;
  evidence?: string;
  // Structured HTTP evidence — three-card Baseline/Payload/Control layout
  // for SQLi and similar; empty/absent for non-HTTP findings (TLS, DNS,
  // InfoDisc). See HttpExchange interface. Added 2026-07-09.
  httpExchanges?: HttpExchange[];
}

export interface VulnerabilityClassType {
  id: string;
  webappid: string;
  testid: string;
  reference: string;
  dateFirstFound: number;
  dateFixed: number;
  dateLastSeen: number;
  vulnLocation: string;
  // Descriptive display heading — set by the pentester via
  // VulnReport.setTitle (e.g. "DNS Zone Transfer (AXFR) Enabled").
  // Empty on legacy records; UI falls back to vulnName when empty.
  // Added 2026-07-09 as part of the aggregator-split rollout.
  title: string;
  // Additional context screenshots beyond the primary `render`. Empty on
  // vulns without extra shots. XSSPage populates this with the vulnerable
  // input + submit button captures. Added 2026-07-09.
  additionalRenders: { label: string; url: string }[];
  owner: string;
  pageUrl: string;
  render: string;
  details: { label: string; value: string }[];
  subFindings: SubFinding[];
  riskRating: string;
  videoURL: string;
  vulnCategory: string;
  vulnName: string;
  vulnStatus: string;
  stepsToReproduce: string[];
  recommendations: string[];
  // null when the backend hasn't reported authentication state for this vuln
  // (older test data or live test in progress). UI should hide the field if null.
  requiresAuthentication: boolean | null;
}

class VulnerabilityClass implements VulnerabilityClassType {
  id: string;
  webappid: string;
  testid: string;
  reference: string;
  dateFirstFound: number;
  dateFixed: number;
  dateLastSeen: number;
  vulnLocation: string;
  title: string;
  additionalRenders: { label: string; url: string }[];
  owner: string;
  pageUrl: string;
  render: string;
  details: { label: string; value: string }[];
  subFindings: SubFinding[];
  riskRating: string;
  videoURL: string;
  vulnCategory: string;
  vulnName: string;
  vulnStatus: string;
  stepsToReproduce: string[];
  recommendations: string[];
  requiresAuthentication: boolean | null;
  // Has this record been populated from a FULL backend payload, as
  // opposed to a slim projection? Detail-bearing fields (subFindings,
  // stepsToReproduce, recommendations, render) are legitimately empty on
  // plenty of real vulns, so their emptiness can't answer "is there more
  // to come?". Only provenance can, and only the detail page needs to
  // ask: it decides between rendering "none recorded" and admitting it
  // is still loading. Mirrors fullResultLoaded on TestResultsClass.
  fullDetailLoaded: boolean;

  // Constructor
  // ------------
  //
  constructor() {
    this.id = EMPTY_STRING;
    this.webappid = EMPTY_STRING;
    this.testid = EMPTY_STRING;
    this.reference = EMPTY_STRING;
    this.dateFirstFound = 0;
    this.dateFixed = 0;
    this.dateLastSeen = 0;
    this.vulnLocation = EMPTY_STRING;
    this.title = EMPTY_STRING;
    this.additionalRenders = [];
    this.owner = EMPTY_STRING;
    this.pageUrl = EMPTY_STRING;
    this.render = EMPTY_STRING;
    this.details = [];
    this.subFindings = [];
    this.riskRating = EMPTY_STRING;
    this.videoURL = EMPTY_STRING;
    this.vulnCategory = EMPTY_STRING;
    this.vulnName = EMPTY_STRING;
    this.vulnStatus = EMPTY_STRING;
    this.stepsToReproduce = [];
    this.recommendations = [];
    this.requiresAuthentication = null;
    this.fullDetailLoaded = false;
  }

  // Set Vulnerability
  // ------------------
  //
  setVulnerability(vulnDictionary: any) {
    if (vulnDictionary) {
      this.id = vulnDictionary.id;
      this.webappid = vulnDictionary.webappid;
      this.testid = vulnDictionary.testid;
      this.reference = vulnDictionary.reference;
      this.dateFirstFound = vulnDictionary.dateFirstFound;
      this.dateFixed = vulnDictionary.dateFixed;
      this.dateLastSeen = vulnDictionary.dateLastSeen;
      this.vulnLocation = vulnDictionary.vulnLocation;
      // title: descriptive display heading. Empty on legacy records —
      // FE display sites fall back to vulnName in that case.
      this.title = vulnDictionary.title || EMPTY_STRING;
      // Additional context screenshots. Filter to entries with a url — a
      // capture that failed will have an empty string that presigning
      // can't do anything with.
      this.additionalRenders = Array.isArray(vulnDictionary.additionalRenders)
        ? vulnDictionary.additionalRenders
            .filter((r: { url?: string }) => r && r.url)
            .map((r: { label?: string; url: string }) => ({
              label: r.label || "",
              url: r.url,
            }))
        : [];
      this.owner = vulnDictionary.owner;
      this.pageUrl = vulnDictionary.pageUrl;
      this.render = vulnDictionary.render;
      this.details = vulnDictionary.details || [];
      // subFindings is the BE's new primary container for what was
      // wrong (see SubFinding interface above). Pre-refactor records
      // come through with this field absent or [] — the
      // legacy-fallback path in the FE render layer falls back to
      // `details` in those cases.
      this.subFindings = Array.isArray(vulnDictionary.subFindings)
        ? vulnDictionary.subFindings
        : [];
      this.riskRating = vulnDictionary.riskRating;
      this.videoURL = vulnDictionary.videoURL;
      // Categories are stored as enum names (e.g. SEC_HEADER_MISCONFIG, CSRF).
      // formatVulnCategory maps each known enum to a human-readable display
      // name and falls back to title-casing snake_case for unknown values.
      this.vulnCategory = formatVulnCategory(vulnDictionary.vulnCategory);
      this.vulnName = convertFromGraphAPItoUI(vulnDictionary.vulnName);
      this.vulnStatus = convertFromGraphAPItoUI(vulnDictionary.vulnStatus);
      this.stepsToReproduce = vulnDictionary.stepsToReproduce || [];
      this.recommendations = vulnDictionary.recommendations || [];
      // null/undefined means "field not present in this payload" — UI hides
      // the row in that case rather than guessing a default.
      this.requiresAuthentication =
        typeof vulnDictionary.requiresAuthn === "boolean"
          ? vulnDictionary.requiresAuthn
          : null;
      // Every caller of this method except createFromSubscription hands
      // over a full record (getTestResults items, quickListVulnerabilitiess
      // items, getVulnerabilities). That one exception clears the flag
      // again immediately below.
      this.fullDetailLoaded = true;
    }
  }

  // Vuln status / risk mutations removed for v1: NIMIS is the sole
  // authority on vuln state. There is no UI surface that mutates either
  // field, so the corresponding GraphQL mutations are also gone from
  // services/graphql/mutations.ts. See the v1 vuln-state-model brief.

  // Create From Subscription
  // -------------------------
  //
  static createFromSubscription(vulnerabilityDict: any) {
    const instance = new VulnerabilityClass();
    instance.setVulnerability(vulnerabilityDict);
    // The discovery stream and the live-findings reconcile both carry a
    // slim projection — enough for a table row, no sub-findings, steps,
    // recommendations or screenshot. Saying otherwise would make the
    // detail page render "none recorded" for detail it simply hasn't
    // asked for yet.
    instance.fullDetailLoaded = false;
    return instance;
  }
}

export default VulnerabilityClass;
